import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "w-full" }
const _hoisted_2 = { class: "space-y-2 sm:space-y-4" }
const _hoisted_3 = { class: "flex items-center gap-2 sm:gap-4" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("div", { class: "text-sm min-w-[40px]" }, "Name", -1)
const _hoisted_5 = { class: "flex items-center gap-2 sm:gap-4" }
const _hoisted_6 = /*#__PURE__*/_createElementVNode("div", { class: "text-sm min-w-[40px]" }, "Value", -1)
const _hoisted_7 = { class: "flex flex-col gap-2 sm:gap-4 h-full" }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("div", { class: "sm:mt-2 mt-1 text-lg font-bold text-white w-full text-start" }, " Image Loading Config ", -1)
const _hoisted_9 = { class: "flex items-center gap-2 sm:gap-4 mt-1 sm:mt-2" }
const _hoisted_10 = { class: "flex justify-between gap-2 bg-white text-black p-4 sm:p-2 rounded-md w-full" }
const _hoisted_11 = { class: "gap-2 flex items-center" }
const _hoisted_12 = /*#__PURE__*/_createElementVNode("div", null, "Lazy Load", -1)
const _hoisted_13 = { class: "flex items-center gap-2 sm:gap-4 mt-1 sm:mt-2" }
const _hoisted_14 = /*#__PURE__*/_createElementVNode("div", { class: "text-lg font-bold text-white w-full text-start" }, "Config Management", -1)
const _hoisted_15 = { class: "min-h-[calc(100vh-16rem)]" }
const _hoisted_16 = { class: "flex items-center gap-2 sm:gap-4" }

import {
	Button,
	Divider,
	Input,
	Modal,
	Popconfirm,
	Switch,
	Table,
	TableColumnsType,
	Tooltip
} from 'ant-design-vue';
import {
	BookOutlined,
	DeleteFilled,
	EditFilled,
	LoadingOutlined,
	PlusOutlined,
	ReloadOutlined
} from '@ant-design/icons-vue';
import { onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { Config, ConfigPaginationRequest } from '@/model/config';
import { Metadata } from '@/model/api';
import {
	getConfigs,
	reloadConfig,
	createConfig as createCfg,
	updateConfig as updateCfg,
	deleteConfig as deleteCfg
} from '@/repository/config';
import { loadConfig } from '@/composables/firebase';
import { config } from '@/config';
import { instance as authenInstance } from '@/repository/authen';
import axiosInstance from '@/composables/_interceptor';
import { assignWithDetail } from '@/util/universal';
import LocalStorageKey from '@/model/key';


export default /*#__PURE__*/_defineComponent({
  __name: 'ConfigView',
  setup(__props) {

const store = useStore();
const route = useRoute();
const router = useRouter();

const columns: TableColumnsType<Config> = [
	{
		title: 'Name',
		dataIndex: 'name',
		key: 'name',
		filterMode: 'tree',
		filterSearch: true,
		onFilter: (value, record) => record.name.startsWith(value as string),
		filters: [],
		// sorter: (a, b) => a.name.length - b.name.length,
		sorter: (a, b) => a.name.localeCompare(b.name),
		sortDirections: ['ascend', 'descend'],
		fixed: 'left'
	},
	{
		title: 'Value',
		dataIndex: 'value',
		key: 'value'
	},
	{
		title: 'Action',
		dataIndex: 'action',
		key: 'action',
		slots: { customRender: 'action' },
		fixed: 'right'
	}
];

const imageLoadingMode = ref(
	assignWithDetail<string>(
		['lazy', 'eager'],
		'lazy',
		localStorage.getItem(LocalStorageKey.ImageLoading)
	)
);

watch(imageLoadingMode, () =>
	localStorage.setItem(LocalStorageKey.ImageLoading, imageLoadingMode.value)
);

let configs = ref<Config[]>([]);
const isFetching = ref(true);
const search = ref(route.query.search?.toString() || '');
const metadata = ref<Metadata>(initialMetadata());
function initialMetadata(): Metadata {
	return {
		limit: Number(route.query.limit) || 10,
		offset: 0,
		totalItem: 0,
		totalPage: 0,
		currentPage: Number(route.query.page) || 1
	};
}

const isReloading = ref(false);
async function reload() {
	isReloading.value = true;

	// reload remote config from firebase
	if (config.env !== 'local') {
		const host = await loadConfig('hostUnikumeKinkando');
		localStorage.setItem('host', host);
		axiosInstance.defaults.baseURL = host;
		authenInstance.defaults.baseURL = host;
	}
	await reloadConfig();

	isReloading.value = false;
}

onMounted(fetchData);

async function fetchData() {
	const query: ConfigPaginationRequest = {
		search: search.value,
		isFetchAll: true
	};
	isFetching.value = true;
	router.replace({
		query: {
			search: search.value || undefined
		}
	});
	try {
		const data = await getConfigs(query);
		if (data) {
			isFetching.value = false;
			configs.value = data.data;
			metadata.value = data.metadata;
			if (metadata.value.totalPage > 0 && (!data.data || !data.data.length)) {
				metadata.value.currentPage = metadata.value.totalPage;
			}
			columns[0].filters = data.data.map((data) => ({ text: data.name, value: data.name }));
		}
	} catch (error) {
		store.dispatch('alert', { type: 'error', message: 'something went wrong, please try again!' });
	}
}

const open = ref(false);
const selectedConfig = ref<Config>({ name: '', value: '' });
const mode = ref<'CREATE' | 'UPDATE' | 'UNKNOWN'>('UNKNOWN');

async function createConfig(config: Config) {
	try {
		await createCfg(config);
		await fetchData();
		open.value = false;
		selectedConfig.value = { name: '', value: '' };
		store.dispatch('alert', { type: 'success', message: 'Add new config successfully!' });
	} catch (error) {
		store.dispatch('alert', { type: 'error', message: 'something went wrong, please try again!' });
	}
}

async function updateConfig(config: Config) {
	try {
		await updateCfg(config);
		await fetchData();
		open.value = false;
		selectedConfig.value = { name: '', value: '' };
		store.dispatch('alert', { type: 'success', message: 'Update config successfully!' });
	} catch (error) {
		store.dispatch('alert', { type: 'error', message: 'something went wrong, please try again!' });
	}
}

async function deleteConfig(name: string) {
	try {
		await deleteCfg(name);
		await fetchData();
		open.value = false;
		selectedConfig.value = { name: '', value: '' };
		store.dispatch('alert', { type: 'success', message: 'Delete config successfully!' });
	} catch (error) {
		store.dispatch('alert', { type: 'error', message: 'something went wrong, please try again!' });
	}
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Modal), {
      open: open.value,
      onCancel: 
			() => {
				open.value = false;
				mode.value = 'UNKNOWN';
			}
		
    }, {
      title: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, _toDisplayString(selectedConfig.value ? 'Update Config' : 'Add Config'), 1),
        _createVNode(_unref(Divider), { class: "p-0 my-4" }),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _hoisted_4,
            _createVNode(_unref(Input), {
              value: selectedConfig.value.name,
              "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((selectedConfig.value.name) = $event)),
              class: "!w-[calc(100%-2rem)] !text-start",
              placeholder: `Enter a config name`,
              disabled: mode.value === 'UPDATE'
            }, null, 8, ["value", "disabled"])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _hoisted_6,
            _createVNode(_unref(Input), {
              value: selectedConfig.value.value,
              "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((selectedConfig.value.value) = $event)),
              class: "!w-[calc(100%-2rem)] !text-start",
              placeholder: `Enter a config value`
            }, null, 8, ["value"])
          ])
        ]),
        _createVNode(_unref(Divider), { class: "p-0 my-4" })
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(Button), {
          key: "back",
          onClick: _cache[2] || (_cache[2] = 
					() => {
						open.value = false;
						mode.value = 'UNKNOWN';
					}
				)
        }, {
          default: _withCtx(() => [
            _createTextVNode(" Cancel ")
          ]),
          _: 1
        }),
        _createVNode(_unref(Button), {
          key: "submit",
          type: "primary",
          onClick: _cache[3] || (_cache[3] = 
					() => (mode.value === 'CREATE' ? createConfig(selectedConfig.value) : updateConfig(selectedConfig.value))
				)
        }, {
          default: _withCtx(() => [
            _createTextVNode(" Submit ")
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["open", "onCancel"]),
    _createElementVNode("div", _hoisted_7, [
      _hoisted_8,
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("div", _hoisted_10, [
          _createElementVNode("div", _hoisted_11, [
            _createVNode(_unref(BookOutlined)),
            _hoisted_12
          ]),
          _createVNode(_unref(Switch), {
            checked: imageLoadingMode.value === 'lazy',
            onChange: _cache[4] || (_cache[4] = (checked) => (imageLoadingMode.value = checked ? 'lazy' : 'eager'))
          }, null, 8, ["checked"])
        ])
      ]),
      _createElementVNode("div", _hoisted_13, [
        _hoisted_14,
        _createVNode(_unref(Button), {
          class: "min-w-[2rem]",
          onClick: _cache[5] || (_cache[5] = 
					() => {
						mode.value = 'CREATE';
						open.value = true;
					}
				)
        }, {
          icon: _withCtx(() => [
            _createVNode(_unref(PlusOutlined))
          ]),
          _: 1
        }),
        _createVNode(_unref(Button), {
          onClick: reload,
          class: "min-w-[2rem]"
        }, {
          icon: _withCtx(() => [
            (!isReloading.value)
              ? (_openBlock(), _createBlock(_unref(ReloadOutlined), { key: 0 }))
              : (_openBlock(), _createBlock(_unref(LoadingOutlined), { key: 1 }))
          ]),
          _: 1
        })
      ]),
      _createElementVNode("div", _hoisted_15, [
        _createVNode(_unref(Table), {
          columns: columns,
          dataSource: _unref(configs),
          loading: isFetching.value,
          style: {"overflow":"auto"}
        }, {
          action: _withCtx(({ record }) => [
            _createElementVNode("div", _hoisted_16, [
              _createVNode(_unref(Tooltip), { placement: "top" }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Button), {
                    class: "text-blue-600",
                    onClick: 
									() => {
										selectedConfig.value = { ...record };
										mode.value = 'UPDATE';
										open.value = true;
									}
								
                  }, {
                    icon: _withCtx(() => [
                      _createVNode(_unref(EditFilled))
                    ]),
                    _: 2
                  }, 1032, ["onClick"])
                ]),
                _: 2
              }, 1024),
              _createVNode(_unref(Popconfirm), {
                title: "Sure to delete this config ?",
                onConfirm: ($event: any) => (deleteConfig(record.name))
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Button), { class: "text-red-600" }, {
                    icon: _withCtx(() => [
                      _createVNode(_unref(DeleteFilled))
                    ]),
                    _: 1
                  })
                ]),
                _: 2
              }, 1032, ["onConfirm"])
            ])
          ]),
          _: 1
        }, 8, ["dataSource", "loading"])
      ])
    ])
  ], 64))
}
}

})